/**
 * Markdown → React elements.
 *
 * This file is a security boundary. Message text, and everything else that
 * reaches it, is written by whoever is on the other end of the chat — on a
 * federated server that includes people on machines this one does not
 * administer. So there is no `dangerouslySetInnerHTML` here, no HTML string is
 * ever produced, and no third-party parser is trusted with the job. The output
 * is React elements whose text children React escapes on the way to the DOM,
 * which means a message can only ever become text, never markup.
 *
 * It is a real tokenizer rather than a chain of `String.replace` calls. The
 * replacement-chain approach looks shorter until somebody pastes
 * `` `**bold**` `` into a code span and the "bold" pass rewrites the inside of
 * the code, which in a developer chat is the case that matters most. Here a
 * code span is consumed whole by the scanner and its contents are never looked
 * at again.
 *
 * The supported syntax is deliberately closed: bold, italic, underline,
 * strikethrough, inline code, fenced code blocks, block quotes, lists, tables,
 * `#channel` references, `[label](url)` links and autolinked URLs.
 * Anything else — images, raw HTML, reference links — is text.
 */

import { Fragment, useContext, useEffect, useState } from 'react'
import type { ReactElement, ReactNode } from 'react'

import { ChannelLinkContext } from '@/lib/channelLinks'

import styles from '@/components/Markdown.module.css'

// ── Options ─────────────────────────────────────────────────────────────────

export interface MarkdownOptions {
  /**
   * The reader, as a username or a qualified `user@server` id. A mention of
   * them is drawn more strongly than a mention of anybody else.
   */
  currentUsername?: string | null
  /**
   * Whether the message really does address everybody, from
   * `Message.mentions_everyone`. Typing `@everyone` without the permission to
   * use it should not light up like a ping that actually happened.
   */
  mentionsEveryone?: boolean
  /** Distinguishes React keys when several documents share one parent. */
  keyPrefix?: string
}

// ── Limits ──────────────────────────────────────────────────────────────────

/**
 * Nesting caps. Both structures recurse, and both are driven entirely by
 * attacker-supplied text, so a message made of ten thousand `>` characters
 * must flatten into something dull rather than exhaust the stack.
 */
const MAX_BLOCK_DEPTH = 6
const MAX_INLINE_DEPTH = 12

/**
 * The only schemes that may become an anchor. `javascript:`, `data:`, `blob:`
 * and everything else stay inert text.
 */
const ALLOWED_SCHEMES: ReadonlySet<string> = new Set(['http:', 'https:', 'mailto:'])

// ── Small helpers ───────────────────────────────────────────────────────────

function cx(...values: Array<string | false | null | undefined>): string {
  return values
    .filter((value): value is string => typeof value === 'string' && value !== '')
    .join(' ')
}

const WORD_CHAR = /[\p{L}\p{N}_]/u

function isWordChar(char: string): boolean {
  return char !== '' && WORD_CHAR.test(char)
}

function isSpace(char: string): boolean {
  return char === ' ' || char === '\t' || char === '\n'
}

/** How many consecutive `char` start at `index`. */
function runLength(text: string, index: number, char: string): number {
  let count = 0
  while (text.charAt(index + count) === char) count += 1
  return count
}

function leadingSpaces(line: string): number {
  let count = 0
  while (count < line.length) {
    const char = line.charAt(count)
    if (char !== ' ' && char !== '\t') break
    count += 1
  }
  return count
}

/**
 * The one place a URL becomes clickable.
 *
 * `new URL` is doing the work on purpose: scheme detection by string prefix is
 * where XSS filters historically go wrong, because `JaVaScRiPt:`, a leading
 * tab and `java&#x0a;script:` all normalise to the same thing in a browser but
 * not in a naive comparison. The URL parser normalises exactly as the
 * navigation would, so the check and the browser agree.
 */
function safeHref(raw: string): string | null {
  let parsed: URL
  try {
    parsed = new URL(raw)
  } catch {
    return null
  }

  return ALLOWED_SCHEMES.has(parsed.protocol) ? parsed.toString() : null
}

// ── Block tokenizer ─────────────────────────────────────────────────────────

type Align = 'start' | 'center' | 'end'

type Block =
  | { kind: 'paragraph'; lines: string[] }
  | { kind: 'code'; language: string; lines: string[] }
  | { kind: 'quote'; blocks: Block[] }
  | { kind: 'list'; ordered: boolean; start: number; items: Block[][] }
  | { kind: 'table'; header: string[]; align: Align[]; rows: string[][] }

const BLANK_LINE = /^[ \t]*$/
const FENCE_LINE = /^ {0,3}(`{3,}|~{3,})(.*)$/
const QUOTE_LINE = /^ {0,3}> ?(.*)$/
const BULLET_ITEM = /^([ \t]*)([-*+])([ \t]+)(.*)$/
const ORDERED_ITEM = /^([ \t]*)(\d{1,9})([.)])([ \t]+)(.*)$/

interface Fence {
  marker: string
  language: string
}

function matchFence(line: string): Fence | null {
  const match = FENCE_LINE.exec(line)
  if (match === null) return null

  const marker = match[1] ?? ''
  const info = match[2] ?? ''

  // A backtick fence cannot carry a backtick in its info string, otherwise
  // ``` `a` ``` on one line would be read as an unterminated block.
  if (marker.charAt(0) === '`' && info.includes('`')) return null

  const language = info.trim().split(/\s+/)[0] ?? ''
  return { marker, language }
}

function isClosingFence(line: string, marker: string): boolean {
  const match = FENCE_LINE.exec(line)
  if (match === null) return false

  const closing = match[1] ?? ''
  if (closing.charAt(0) !== marker.charAt(0)) return false
  if (closing.length < marker.length) return false

  return (match[2] ?? '').trim() === ''
}

interface ListItemMatch {
  ordered: boolean
  /** Characters of leading whitespace before the marker. */
  indent: number
  /** Character offset at which the item's own content begins. */
  contentIndent: number
  number: number
  content: string
}

function matchListItem(line: string): ListItemMatch | null {
  const ordered = ORDERED_ITEM.exec(line)
  if (ordered !== null) {
    const indent = (ordered[1] ?? '').length
    const digits = ordered[2] ?? '1'
    const gap = (ordered[4] ?? ' ').length
    const parsed = Number.parseInt(digits, 10)

    return {
      ordered: true,
      indent,
      contentIndent: indent + digits.length + 1 + gap,
      number: Number.isFinite(parsed) ? parsed : 1,
      content: ordered[5] ?? '',
    }
  }

  const bullet = BULLET_ITEM.exec(line)
  if (bullet !== null) {
    const indent = (bullet[1] ?? '').length
    const gap = (bullet[3] ?? ' ').length

    return {
      ordered: false,
      indent,
      contentIndent: indent + 1 + gap,
      number: 1,
      content: bullet[4] ?? '',
    }
  }

  return null
}

/**
 * A table, in the one shape the composer writes and the other clients render:
 * a header row, a delimiter row, and body rows, all pipe-separated.
 *
 * The header decides the width. A row with more cells than the header has its
 * tail dropped and a shorter row is padded, so a malformed table is a plain
 * table rather than a ragged grid — and, more to the point, so the number of
 * cells rendered can never be driven past the number the header declared.
 */
const TABLE_MAX_COLUMNS = 8

const DELIMITER_CELL = /^:?-{1,}:?$/

/** Splits on unescaped pipes, so `\|` can appear inside a cell. */
function splitRow(line: string): string[] {
  const cells: string[] = []
  let cell = ''
  let index = 0

  const trimmed = line.trim()
  const start = trimmed.startsWith('|') ? 1 : 0
  const end = trimmed.length - (trimmed.length > 1 && trimmed.endsWith('|') && !trimmed.endsWith('\\|') ? 1 : 0)
  const body = trimmed.slice(start, end)

  while (index < body.length) {
    const char = body.charAt(index)

    if (char === '\\' && body.charAt(index + 1) === '|') {
      cell += '|'
      index += 2
      continue
    }

    if (char === '|') {
      cells.push(cell.trim())
      cell = ''
      index += 1
      continue
    }

    cell += char
    index += 1
  }

  cells.push(cell.trim())
  return cells
}

function alignOf(cell: string): Align {
  const left = cell.startsWith(':')
  const right = cell.endsWith(':')
  if (left && right) return 'center'
  if (right) return 'end'
  return 'start'
}

function isTableLine(line: string): boolean {
  return line.includes('|') && !BLANK_LINE.test(line)
}

interface TableScan {
  block: Block
  next: number
}

function matchTable(lines: readonly string[], index: number): TableScan | null {
  const headerLine = lines[index] ?? ''
  const delimiterLine = lines[index + 1] ?? ''
  if (!isTableLine(headerLine) || !isTableLine(delimiterLine)) return null

  const headerCells = splitRow(headerLine)
  const delimiters = splitRow(delimiterLine)
  // The two rows must agree on the width before this is a table at all;
  // otherwise a sentence with a pipe in it above a line of dashes would be one.
  if (delimiters.length < 2 || delimiters.length !== headerCells.length) return null
  if (!delimiters.every((cell) => DELIMITER_CELL.test(cell))) return null

  const header = headerCells.slice(0, TABLE_MAX_COLUMNS)

  const align = delimiters.slice(0, header.length).map(alignOf)
  const rows: string[][] = []
  let cursor = index + 2

  while (cursor < lines.length) {
    const line = lines[cursor] ?? ''
    if (!isTableLine(line)) break
    const cells = splitRow(line)
    rows.push(
      Array.from({ length: header.length }, (_unused, column) => cells[column] ?? ''),
    )
    cursor += 1
  }

  return { block: { kind: 'table', header, align, rows }, next: cursor }
}

function startsNewBlock(line: string, depth: number): boolean {
  if (matchFence(line) !== null) return true
  if (depth >= MAX_BLOCK_DEPTH) return false
  return QUOTE_LINE.test(line) || matchListItem(line) !== null
}

interface ListScan {
  block: Block
  next: number
}

function collectList(
  lines: readonly string[],
  from: number,
  first: ListItemMatch,
  depth: number,
): ListScan {
  const items: Block[][] = []
  let current: string[] = []
  let contentIndent = first.contentIndent
  let index = from
  let pendingBlank = false

  const flush = (): void => {
    if (current.length > 0) items.push(tokenizeBlocks(current, depth + 1))
    current = []
  }

  while (index < lines.length) {
    const line = lines[index] ?? ''

    if (BLANK_LINE.test(line)) {
      // A blank line inside a list is only a separator until the next line
      // proves the list has actually ended.
      pendingBlank = true
      index += 1
      continue
    }

    const item = matchListItem(line)

    if (item !== null && item.ordered === first.ordered && item.indent <= first.indent + 1) {
      flush()
      contentIndent = item.contentIndent
      current.push(item.content)
      pendingBlank = false
      index += 1
      continue
    }

    const spaces = leadingSpaces(line)
    const indentedUnderItem = spaces >= contentIndent
    const lazyContinuation = !pendingBlank && item === null

    if (indentedUnderItem || lazyContinuation) {
      if (pendingBlank) current.push('')
      current.push(line.slice(Math.min(spaces, contentIndent)))
      pendingBlank = false
      index += 1
      continue
    }

    break
  }

  flush()

  return {
    block: { kind: 'list', ordered: first.ordered, start: first.number, items },
    next: index,
  }
}

function tokenizeBlocks(lines: readonly string[], depth: number): Block[] {
  const blocks: Block[] = []
  let index = 0

  while (index < lines.length) {
    const line = lines[index] ?? ''

    if (BLANK_LINE.test(line)) {
      index += 1
      continue
    }

    const fence = matchFence(line)
    if (fence !== null) {
      const body: string[] = []
      index += 1

      while (index < lines.length) {
        const current = lines[index] ?? ''
        if (isClosingFence(current, fence.marker)) {
          index += 1
          break
        }
        body.push(current)
        index += 1
      }

      // An unterminated fence still becomes a code block: the alternative is
      // re-reading everything after it as prose, which is worse for the very
      // common case of a half-typed message.
      blocks.push({ kind: 'code', language: fence.language, lines: body })
      continue
    }

    const table = matchTable(lines, index)
    if (table !== null) {
      blocks.push(table.block)
      index = table.next
      continue
    }

    if (depth < MAX_BLOCK_DEPTH) {
      if (QUOTE_LINE.test(line)) {
        const inner: string[] = []

        while (index < lines.length) {
          const match = QUOTE_LINE.exec(lines[index] ?? '')
          if (match === null) break
          inner.push(match[1] ?? '')
          index += 1
        }

        blocks.push({ kind: 'quote', blocks: tokenizeBlocks(inner, depth + 1) })
        continue
      }

      const item = matchListItem(line)
      if (item !== null) {
        const scanned = collectList(lines, index, item, depth)
        blocks.push(scanned.block)
        index = scanned.next
        continue
      }
    }

    const paragraph: string[] = []
    while (index < lines.length) {
      const current = lines[index] ?? ''
      if (BLANK_LINE.test(current)) break
      if (paragraph.length > 0 && startsNewBlock(current, depth)) break
      // A table needs the line after it to be recognised, so it cannot be
      // folded into `startsNewBlock`, which only ever sees one line.
      if (paragraph.length > 0 && matchTable(lines, index) !== null) break
      paragraph.push(current.trim())
      index += 1
    }

    if (paragraph.length > 0) blocks.push({ kind: 'paragraph', lines: paragraph })
  }

  return blocks
}

// ── Inline scanner ──────────────────────────────────────────────────────────

interface RenderContext {
  currentUsername: string | null
  mentionsEveryone: boolean
  prefix: string
  counter: { value: number }
}

function nextKey(ctx: RenderContext, tag: string): string {
  ctx.counter.value += 1
  return `${ctx.prefix}-${tag}-${ctx.counter.value}`
}

interface Consumed {
  node: ReactNode
  next: number
}

/**
 * Reads a code span starting at a run of backticks, or returns null if the run
 * is never closed by one of exactly the same length. Everything between the
 * fences is literal, which is what makes `` `**not bold**` `` behave.
 */
function readCodeSpan(text: string, index: number): { content: string; next: number } | null {
  const fence = runLength(text, index, '`')
  let cursor = index + fence

  while (cursor < text.length) {
    if (text.charAt(cursor) !== '`') {
      cursor += 1
      continue
    }

    const run = runLength(text, cursor, '`')
    if (run !== fence) {
      cursor += run
      continue
    }

    let content = text.slice(index + fence, cursor)
    // One padding space on each side is stripped, so `` ` `` can be written as
    // ``` `` ` `` ```.
    if (
      content.length > 2 &&
      content.startsWith(' ') &&
      content.endsWith(' ') &&
      content.trim() !== ''
    ) {
      content = content.slice(1, -1)
    }

    return { content, next: cursor + run }
  }

  return null
}

interface Delimiter {
  chars: string
  tag: 'strong' | 'em' | 'u' | 'del'
  /**
   * True for `_` and `__`, which must not fire inside a word. Without this,
   * `some_variable_name` becomes italic — unacceptable in a chat where people
   * paste identifiers all day. `*` has no such rule because intraword
   * asterisks are vanishingly rare in prose.
   */
  wordBoundary: boolean
}

function delimiterAt(text: string, index: number): Delimiter | null {
  const pair = text.slice(index, index + 2)
  if (pair === '~~') return { chars: '~~', tag: 'del', wordBoundary: false }
  if (pair === '++') return { chars: '++', tag: 'u', wordBoundary: false }
  if (pair === '**') return { chars: '**', tag: 'strong', wordBoundary: false }
  if (pair === '__') return { chars: '__', tag: 'strong', wordBoundary: true }

  const single = text.charAt(index)
  if (single === '*') return { chars: '*', tag: 'em', wordBoundary: false }
  if (single === '_') return { chars: '_', tag: 'em', wordBoundary: true }

  return null
}

/**
 * Finds the matching closing delimiter, stepping over code spans so that a
 * delimiter inside backticks can never close one outside them, and over runs
 * of the wrong length so that `*a **b** c*` nests instead of tearing.
 */
function findClosing(text: string, from: number, delim: Delimiter): number {
  const marker = delim.chars.charAt(0)
  let index = from

  while (index < text.length) {
    const char = text.charAt(index)

    if (char === '`') {
      const span = readCodeSpan(text, index)
      if (span !== null) {
        index = span.next
        continue
      }
    }

    if (char !== marker) {
      index += 1
      continue
    }

    const run = runLength(text, index, marker)
    if (run === delim.chars.length) {
      const before = text.charAt(index - 1)
      const after = text.charAt(index + run)
      const closesTightly = before !== '' && !isSpace(before)
      const respectsWord = !delim.wordBoundary || !isWordChar(after)

      if (closesTightly && respectsWord) return index
    }

    index += run
  }

  return -1
}

function readEmphasis(
  text: string,
  index: number,
  ctx: RenderContext,
  depth: number,
  insideLink: boolean,
): Consumed | null {
  if (depth >= MAX_INLINE_DEPTH) return null

  const delim = delimiterAt(text, index)
  if (delim === null) return null

  const marker = delim.chars.charAt(0)
  // `***x***` and friends: a run that is not exactly the delimiter is left as
  // text rather than guessed at.
  if (runLength(text, index, marker) !== delim.chars.length) return null
  if (delim.wordBoundary && isWordChar(text.charAt(index - 1))) return null

  const start = index + delim.chars.length
  const opener = text.charAt(start)
  if (opener === '' || isSpace(opener)) return null

  const close = findClosing(text, start, delim)
  if (close < 0) return null

  const children = parseInline(text.slice(start, close), ctx, depth + 1, insideLink)
  const key = nextKey(ctx, delim.tag)

  const node =
    delim.tag === 'strong' ? (
      <strong key={key}>{children}</strong>
    ) : delim.tag === 'em' ? (
      <em key={key}>{children}</em>
    ) : delim.tag === 'u' ? (
      <u key={key} className={styles.underline}>
        {children}
      </u>
    ) : (
      <del key={key} className={styles.strike}>
        {children}
      </del>
    )

  return { node, next: close + delim.chars.length }
}

// `@name`, optionally federated as `@name@server.example`.
const MENTION_TOKEN =
  /@([a-z0-9](?:[a-z0-9._-]{0,30}[a-z0-9])?)(?:@([a-z0-9](?:[a-z0-9-]*[a-z0-9])?(?:\.[a-z0-9](?:[a-z0-9-]*[a-z0-9])?)+))?/iy

function isMentionStart(text: string, index: number): boolean {
  const before = text.charAt(index - 1)
  // `chris@example.com` must not produce a mention of `example.com`.
  return before === '' || (!isWordChar(before) && before !== '@')
}

function isSelfMention(handle: string, currentUsername: string | null): boolean {
  if (currentUsername === null || currentUsername === '') return false

  const me = currentUsername.toLowerCase()
  const bare = me.split('@')[0] ?? me

  // A bare `@chris` is the local user; a qualified `@chris@other.example` is
  // somebody else with the same name on another server.
  return handle === me || handle === bare
}

function readMention(text: string, index: number, ctx: RenderContext): Consumed | null {
  MENTION_TOKEN.lastIndex = index
  const match = MENTION_TOKEN.exec(text)
  if (match === null) return null

  const name = match[1] ?? ''
  const domain = match[2]
  const label = domain === undefined ? `@${name}` : `@${name}@${domain}`
  const handle = domain === undefined ? name.toLowerCase() : `${name}@${domain}`.toLowerCase()
  const lowered = name.toLowerCase()

  const addressesEveryone =
    ctx.mentionsEveryone && domain === undefined && (lowered === 'everyone' || lowered === 'here')
  const self = addressesEveryone || isSelfMention(handle, ctx.currentUsername)

  return {
    node: (
      <span
        key={nextKey(ctx, 'mention')}
        className={cx(styles.mention, self && styles.mentionSelf)}
      >
        {label}
      </span>
    ),
    next: index + (match[0] ?? '').length,
  }
}

/**
 * `#channel`, in the spelling the server accepts for a slug.
 *
 * A reference is only ever drawn as a link when the reader's own room list
 * already contains a channel by that name. Anything else — a private channel
 * they are not in, an issue number, a colour — stays exactly as typed.
 */
const CHANNEL_TOKEN = /#([a-z0-9](?:[a-z0-9-]{0,62}[a-z0-9])?)/iy

function isChannelStart(text: string, index: number): boolean {
  const before = text.charAt(index - 1)
  return before === '' || (!isWordChar(before) && before !== '#')
}

function ChannelReference({ name, label }: { name: string; label: string }): ReactElement {
  const links = useContext(ChannelLinkContext)
  const target = links?.resolve(name) ?? null

  if (links === null || target === null) return <>{label}</>

  return (
    <a
      className={styles.channel}
      href={`/rooms/${encodeURIComponent(target.roomId)}`}
      onClick={(event) => {
        // Left click stays inside the app; modified clicks and middle clicks
        // keep the browser's own meaning, which the href is there to serve.
        if (event.defaultPrevented) return
        if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return
        if (event.button !== 0) return
        event.preventDefault()
        links.open(target.roomId)
      }}
    >
      {label}
    </a>
  )
}

function readChannel(text: string, index: number, ctx: RenderContext): Consumed | null {
  CHANNEL_TOKEN.lastIndex = index
  const match = CHANNEL_TOKEN.exec(text)
  if (match === null) return null

  const name = match[1] ?? ''
  const whole = match[0] ?? ''

  return {
    node: <ChannelReference key={nextKey(ctx, 'channel')} name={name} label={whole} />,
    next: index + whole.length,
  }
}

// Autolinks. A generic scheme is matched and then vetted by `safeHref`, so an
// unwelcome one is recognised and rejected rather than quietly slipping past a
// narrower pattern.
const URL_TOKEN = /(?:[a-z][a-z0-9+.-]*:\/\/|mailto:|www\.)[^\s<>`]+/iy

const TRAILING_PUNCTUATION = '.,;:!?*_~\'"'

function countChar(text: string, char: string): number {
  let count = 0
  for (const current of text) {
    if (current === char) count += 1
  }
  return count
}

/**
 * Sentences end in punctuation and URLs get wrapped in brackets, so the last
 * characters of a match are usually not part of the address.
 */
function trimUrlTail(candidate: string): string {
  let value = candidate

  while (value.length > 0) {
    const last = value.charAt(value.length - 1)

    if (last === ')' && countChar(value, ')') > countChar(value, '(')) {
      value = value.slice(0, -1)
      continue
    }

    if (TRAILING_PUNCTUATION.includes(last)) {
      value = value.slice(0, -1)
      continue
    }

    break
  }

  return value
}

function isLinkStart(text: string, index: number): boolean {
  const char = text.charAt(index)
  if (!/[a-z]/i.test(char)) return false

  const before = text.charAt(index - 1)
  return before === '' || (!isWordChar(before) && before !== '/' && before !== ':')
}

function readLink(text: string, index: number, ctx: RenderContext): Consumed | null {
  URL_TOKEN.lastIndex = index
  const match = URL_TOKEN.exec(text)
  if (match === null) return null

  const candidate = trimUrlTail(match[0] ?? '')
  if (candidate === '') return null

  const href = safeHref(/^www\./i.test(candidate) ? `https://${candidate}` : candidate)
  // Not a scheme we are willing to hand to the browser: the text stays text.
  if (href === null) return null

  return {
    node: (
      <a
        key={nextKey(ctx, 'link')}
        className={styles.link}
        href={href}
        target="_blank"
        // `noopener`/`noreferrer` keep the opened page from reaching back
        // through `window.opener` or learning which conversation sent it, and
        // `nofollow` stops the chat from lending anybody its reputation. The
        // preview metadata and thumbnails are fetched by the Space server;
        // the message renderer never contacts the pasted host directly.
        rel="noopener noreferrer nofollow"
      >
        {candidate}
      </a>
    ),
    next: index + candidate.length,
  }
}

/**
 * `[label](url)`.
 *
 * The label is attacker-controlled and may claim anything, so the destination
 * is put in the title as well: a link that says "the invoice" and points at
 * another host should be recognisable as one without following it. The URL
 * itself goes through the same `safeHref` gate as an autolink, and a rejected
 * scheme leaves the whole construct as literal text.
 */
const LABELLED_LINK = /\[([^\]\n]{0,300})\]\(\s*([^()\s]{1,2000})\s*\)/y

function readLabelledLink(
  text: string,
  index: number,
  ctx: RenderContext,
  depth: number,
): Consumed | null {
  if (depth >= MAX_INLINE_DEPTH) return null

  LABELLED_LINK.lastIndex = index
  const match = LABELLED_LINK.exec(text)
  if (match === null) return null

  const label = match[1] ?? ''
  const raw = match[2] ?? ''
  if (label.trim() === '') return null

  const href = safeHref(/^www\./i.test(raw) ? `https://${raw}` : raw)
  if (href === null) return null

  return {
    node: (
      <a
        key={nextKey(ctx, 'labelled')}
        className={styles.link}
        href={href}
        title={href}
        target="_blank"
        rel="noopener noreferrer nofollow"
      >
        {parseInline(label, ctx, depth + 1, true)}
      </a>
    ),
    next: index + (match[0] ?? '').length,
  }
}

/**
 * `insideLink` suppresses everything that would produce a second anchor.
 * Nested anchors are invalid HTML — a browser closes the outer one early — so
 * `[see https://a.test](https://b.test)` would otherwise render as two links,
 * the second of which nobody wrote.
 */
function parseInline(
  text: string,
  ctx: RenderContext,
  depth: number,
  insideLink = false,
): ReactNode[] {
  const nodes: ReactNode[] = []
  let buffer = ''
  let index = 0

  const flush = (): void => {
    if (buffer !== '') {
      nodes.push(buffer)
      buffer = ''
    }
  }

  while (index < text.length) {
    const char = text.charAt(index)

    if (char === '`') {
      const span = readCodeSpan(text, index)
      if (span !== null) {
        flush()
        nodes.push(
          <code key={nextKey(ctx, 'code')} className={styles.code}>
            {span.content}
          </code>,
        )
        index = span.next
        continue
      }
    }

    if (char === '*' || char === '_' || char === '~' || char === '+') {
      const emphasis = readEmphasis(text, index, ctx, depth, insideLink)
      if (emphasis !== null) {
        flush()
        nodes.push(emphasis.node)
        index = emphasis.next
        continue
      }
    }

    if (char === '@' && isMentionStart(text, index)) {
      const mention = readMention(text, index, ctx)
      if (mention !== null) {
        flush()
        nodes.push(mention.node)
        index = mention.next
        continue
      }
    }

    if (char === '[' && !insideLink) {
      const labelled = readLabelledLink(text, index, ctx, depth)
      if (labelled !== null) {
        flush()
        nodes.push(labelled.node)
        index = labelled.next
        continue
      }
    }

    if (char === '#' && !insideLink && isChannelStart(text, index)) {
      const channel = readChannel(text, index, ctx)
      if (channel !== null) {
        flush()
        nodes.push(channel.node)
        index = channel.next
        continue
      }
    }

    if (!insideLink && isLinkStart(text, index)) {
      const link = readLink(text, index, ctx)
      if (link !== null) {
        flush()
        nodes.push(link.node)
        index = link.next
        continue
      }
    }

    buffer += char
    index += 1
  }

  flush()
  return nodes
}

// ── Rendering ───────────────────────────────────────────────────────────────

function renderLines(lines: readonly string[], ctx: RenderContext): ReactNode {
  return lines.map((line, position) => (
    <Fragment key={nextKey(ctx, 'line')}>
      {position > 0 ? <br /> : null}
      {parseInline(line, ctx, 0)}
    </Fragment>
  ))
}

/** A single-paragraph item renders bare, which is what keeps tight lists tight. */
function renderItem(blocks: readonly Block[], ctx: RenderContext): ReactNode {
  const only = blocks.length === 1 ? blocks[0] : undefined
  if (only !== undefined && only.kind === 'paragraph') return renderLines(only.lines, ctx)
  return renderBlocks(blocks, ctx)
}

type CopyState = 'idle' | 'copied' | 'failed'

function copyText(text: string): Promise<void> {
  if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText !== undefined) {
    return navigator.clipboard.writeText(text)
  }

  if (typeof document === 'undefined') return Promise.reject(new Error('Clipboard unavailable'))

  const source = document.createElement('textarea')
  source.value = text
  source.setAttribute('readonly', '')
  source.style.position = 'fixed'
  source.style.opacity = '0'
  document.body.append(source)
  source.select()

  try {
    const copied = document.execCommand('copy')
    source.remove()
    return copied ? Promise.resolve() : Promise.reject(new Error('Copy failed'))
  } catch (error) {
    source.remove()
    return Promise.reject(error)
  }
}

function CodeBlock({ language, code }: { language: string; code: string }): ReactElement {
  const [copyState, setCopyState] = useState<CopyState>('idle')

  useEffect(() => {
    if (copyState === 'idle') return
    const timer = window.setTimeout(() => setCopyState('idle'), 2_000)
    return () => window.clearTimeout(timer)
  }, [copyState])

  const label = copyState === 'copied' ? 'Copied' : copyState === 'failed' ? 'Copy failed' : 'Copy'

  return (
    <div className={styles.codeBlock}>
      <div className={styles.codeHeader}>
        <span className={styles.codeLanguage}>{language === '' ? 'code' : language}</span>
        <button
          className={styles.copyCode}
          type="button"
          onClick={() => {
            void copyText(code).then(
              () => setCopyState('copied'),
              () => setCopyState('failed'),
            )
          }}
        >
          {label}
        </button>
      </div>
      <pre className={styles.pre} tabIndex={0} aria-label={language === '' ? 'Code block' : `${language} code block`}>
        <code>{code}</code>
      </pre>
    </div>
  )
}

function renderBlock(block: Block, ctx: RenderContext): ReactNode {
  switch (block.kind) {
    case 'paragraph':
      return (
        <p key={nextKey(ctx, 'p')} className={styles.paragraph}>
          {renderLines(block.lines, ctx)}
        </p>
      )

    case 'code':
      return <CodeBlock key={nextKey(ctx, 'fence')} language={block.language} code={block.lines.join('\n')} />

    case 'quote':
      return (
        <blockquote key={nextKey(ctx, 'quote')} className={styles.quote}>
          {renderBlocks(block.blocks, ctx)}
        </blockquote>
      )

    case 'list': {
      const items = block.items.map((item) => (
        <li key={nextKey(ctx, 'li')} className={styles.listItem}>
          {renderItem(item, ctx)}
        </li>
      ))

      return block.ordered ? (
        <ol key={nextKey(ctx, 'ol')} className={styles.list} start={block.start}>
          {items}
        </ol>
      ) : (
        <ul key={nextKey(ctx, 'ul')} className={styles.list}>
          {items}
        </ul>
      )
    }

    case 'table': {
      const align = block.align
      const hasHeader = block.header.some((cell) => cell.trim() !== '')

      return (
        // The wrapper scrolls, never the conversation: a table wider than the
        // column must not push the whole message list sideways.
        <div key={nextKey(ctx, 'tablewrap')} className={styles.tableWrap}>
          <table className={styles.table}>
            {hasHeader ? (
              <thead>
                <tr>
                  {block.header.map((cell, column) => (
                    <th
                      key={nextKey(ctx, 'th')}
                      scope="col"
                      style={{ textAlign: align[column] ?? 'start' }}
                    >
                      {parseInline(cell, ctx, 0)}
                    </th>
                  ))}
                </tr>
              </thead>
            ) : null}
            <tbody>
              {block.rows.map((row) => (
                <tr key={nextKey(ctx, 'tr')}>
                  {row.map((cell, column) => (
                    <td
                      key={nextKey(ctx, 'td')}
                      style={{ textAlign: align[column] ?? 'start' }}
                    >
                      {parseInline(cell, ctx, 0)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )
    }

    default:
      return null
  }
}

function renderBlocks(blocks: readonly Block[], ctx: RenderContext): ReactNode[] {
  return blocks.map((block) => renderBlock(block, ctx))
}

// ── Entry points ────────────────────────────────────────────────────────────

/**
 * Parses `text` and returns React elements. Never returns, produces or accepts
 * an HTML string.
 */
export function renderMarkdown(text: string, options: MarkdownOptions = {}): ReactNode {
  const ctx: RenderContext = {
    currentUsername: options.currentUsername ?? null,
    mentionsEveryone: options.mentionsEveryone ?? false,
    prefix: options.keyPrefix ?? 'md',
    counter: { value: 0 },
  }

  const normalised = text.replace(/\r\n?/g, '\n')
  return renderBlocks(tokenizeBlocks(normalised.split('\n'), 0), ctx)
}

/**
 * The same syntax without block structure, for the places that have one line
 * to work with: a search snippet, a room topic, a notification preview.
 */
export function renderInlineMarkdown(text: string, options: MarkdownOptions = {}): ReactNode {
  const ctx: RenderContext = {
    currentUsername: options.currentUsername ?? null,
    mentionsEveryone: options.mentionsEveryone ?? false,
    prefix: options.keyPrefix ?? 'mdi',
    counter: { value: 0 },
  }

  return parseInline(text.replace(/\s+/g, ' ').trim(), ctx, 0)
}

/**
 * Whether a URL would be turned into a link. Exported so that other surfaces
 * can apply the same rule instead of inventing a second, weaker one.
 */
export function isSafeLink(raw: string): boolean {
  return safeHref(raw) !== null
}
